'use client';

import { c as _c } from "react/compiler-runtime";
import { useMandatoryContext } from '@repo/hooks';
import { createContext, memo, type PropsWithChildren } from 'react';
import type { SharedErrorCode } from '@/lib/orpc/errors';
import type { MerklUserReward } from '@/lib/procedures/userRewards.contract';
import { type RewardsTableRowData, useRewardsTable } from '@/hooks/reward/useRewardsTable';
export interface RewardContextProps {
  display: {
    rewardsMerkl: RewardsTableRowData[];
    rewardsMerged: RewardsTableRowData[];
  };
  txData: {
    rewardsMerkl: MerklUserReward[];
  };
  loading: boolean;
  /** The shared error code of the failed rewards query, `null` when it loaded. */
  errorCode: SharedErrorCode | 'UNKNOWN' | null;
  refetchRewards: () => void;
}
export const RewardContext = createContext<RewardContextProps | null>(null);

/**
 * Hook to access reward context with Morpho and Merkl reward data.
 * @returns Reward context with display data, transaction data, loading state, and refetch function
 * @throws Error if used outside RewardContextProvider
 */
export const useRewardContext = () => {
  return useMandatoryContext(RewardContext, "RewardContext");
};
export type RewardProviderProps = PropsWithChildren;
const RewardContextProvider = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  const reward = useRewardsTable();
  let t1;
  if ($[0] !== reward) {
    t1 = {
      ...reward
    };
    $[0] = reward;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <RewardContext.Provider value={t1}>{children}</RewardContext.Provider>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export default memo(RewardContextProvider);